import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import * as Styled from './MParagraph.styles';

import { MParagraphProps, MParagraphAlignment } from './MParagraph.types';
import { ABody, ABodySize, ABodyColor } from '../../atoms/ABody';
import { ALinkStandalone, ALinkStandaloneSize } from '../../atoms/ALinkStandalone';
import { DEFAULT_SEE_MORE_TEXT, DEFAULT_SEE_LESS_TEXT } from '../../utils/i18nConstants';
import { useOverflowDetection } from '../../hooks';

const COMPONENT_NAME = 'm-web-ui-kit-MParagraph';
const TEST_ID = 'web-ui-kit-MParagraph';

export const MParagraphDEFAULT_I18N = {
  seeMore: DEFAULT_SEE_MORE_TEXT,
  seeLess: DEFAULT_SEE_LESS_TEXT,
} as const;

export const MParagraph: React.FC<MParagraphProps> = ({
  alignment = MParagraphAlignment.Left,
  truncated = false,
  truncateLines = 3,
  expanded = false,
  bodySize: bodySizeProp = ABodySize.S,
  bodyColor: bodyColorProp = ABodyColor.Default,
  truncationLinkSize: truncationLinkSizeProp = ALinkStandaloneSize.Small,
  children,
  aBody,
  aLinkStandalone,
  className,
  i18nProperties,
  custom_click_track_value,
}) => {
  const [isExpanded, setExpanded] = useState(truncated ? expanded : true);
  const fullText = children;
  const truncateClass = `m-ellipsis-${truncateLines > 1 ? Math.min(truncateLines, 11) + 'lines' : '1line'}`;
  const paragraphRef = useRef<HTMLDivElement>(null);

  // Prefer new grouped props and fall back to legacy props for backwards compatibility.
  const bodySize = aBody?.size ?? bodySizeProp;
  const bodyColor = aBody?.color ?? bodyColorProp;
  const bodyText = aBody?.bodyText ?? fullText ?? '';
  const linkSize = aLinkStandalone?.size ?? truncationLinkSizeProp;
  const linkText = aLinkStandalone?.text ?? i18nProperties?.seeMore ?? MParagraphDEFAULT_I18N.seeMore;
  const linkExternalLink = aLinkStandalone?.externalLink ?? false;

  // Overflow measurement is the single source of truth for showing the control
  // in the collapsed state, so it never appears for text that fits the clamp.
  // Once expanded, keep the control mounted so the user can collapse again.
  const isOverflowing = useOverflowDetection(paragraphRef, truncated);
  const showTruncationControl = truncated && (isOverflowing || isExpanded);

  useEffect(() => {
    setExpanded(truncated ? expanded : true);
  }, [expanded, truncated]);

  function handleClick(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    setExpanded(!isExpanded);
  }

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      alignment={alignment}
      className={className}
    >
      <Styled.ParagraphContent ref={paragraphRef} className={clsx(!isExpanded && truncated && truncateClass)}>
        <ABody bodyText={bodyText} size={bodySize} color={bodyColor} />
      </Styled.ParagraphContent>
      {showTruncationControl && (
        <Styled.TruncationControl>
          <ALinkStandalone
            onClick={handleClick}
            href="#"
            size={linkSize}
            externalLink={linkExternalLink}
            custom_click_track_value={
              isExpanded ? custom_click_track_value?.seeLess : custom_click_track_value?.seeMore
            }
          >
            {isExpanded ? i18nProperties?.seeLess ?? MParagraphDEFAULT_I18N.seeLess : linkText}
          </ALinkStandalone>
        </Styled.TruncationControl>
      )}
    </Styled.Wrapper>
  );
};

export { MParagraphAlignment, TEST_ID as M_PARAGRAPH_TEST_ID };
